// Shared chrome for the Teams settings panel.
//
// The settings dialog has an established visual language (see
// UsageAndCreditsSection): a muted section label, rounded translucent cards,
// a white primary button and a destructive red one. These helpers exist so the
// six teams views share it exactly rather than each re-deriving a stack of
// Tailwind classes — and so a change to the panel's look is one edit.

import type { ReactNode, Ref } from 'react'
import { ChevronLeft } from 'lucide-react'

import { cn } from '../../lib/utils'

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="pb-1.5 text-sm font-semibold leading-5 text-[rgba(229,231,235,0.53)]">
      {children}
    </p>
  )
}

export function Card({
  children,
  className,
  testId,
}: {
  children: ReactNode
  className?: string
  testId?: string
}) {
  return (
    <div data-testid={testId} className={cn('overflow-hidden rounded-2xl bg-white/[0.06]', className)}>
      {children}
    </div>
  )
}

export function BackButton({
  label,
  onClick,
  testId,
}: {
  label: string
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="inline-flex w-fit cursor-pointer items-center gap-1 text-sm text-white/60 hover:text-white"
    >
      <ChevronLeft className="h-4 w-4" />
      {label}
    </button>
  )
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  testId,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  testId: string
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-9 cursor-pointer rounded-full bg-white px-5 text-sm font-semibold text-black',
        'transition-colors hover:bg-white/90',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  )
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
  testId,
  buttonRef,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  testId: string
  buttonRef?: Ref<HTMLButtonElement>
  type?: 'button' | 'submit'
}) {
  return (
    <button
      ref={buttonRef}
      type={type}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-9 cursor-pointer rounded-full bg-white/[0.12] px-5 text-sm font-medium text-white',
        'transition-colors hover:bg-white/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  )
}

export function DestructiveButton({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-9 cursor-pointer rounded-full bg-destructive px-5 text-sm font-semibold text-white',
        'transition-colors hover:bg-destructive/90',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  )
}

/**
 * The standing warning block used by every irreversible teams action.
 *
 * Its own component because RFC-1371 D3 and RFC-1372 C7 both require the
 * consequence be stated BEFORE the action, and a consistent red slab is how the
 * user learns to read those two sentences rather than skip them.
 */
export function WarningPanel({
  children,
  testId,
}: {
  children: ReactNode
  testId: string
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm leading-6 text-white/85"
    >
      {children}
    </div>
  )
}

export function ErrorText({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <p data-testid={testId} className="text-sm text-destructive">
      {children}
    </p>
  )
}

export function EmptyState({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <p
      data-testid={testId}
      className="rounded-2xl bg-white/[0.04] px-4 py-6 text-center text-sm text-white/45"
    >
      {children}
    </p>
  )
}
